














































import { defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { CreditCardAssignment } from "@scrile/api-provider/dist/api/CreditCardAssignmentsProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useOnboarding from "@/hooks/useOnboarding";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import useSystemSettings from "@/hooks/useSystemSettings";
import { yMetrics } from "@/lib/analytics";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getCost, getCostByCurrency, formatMoneyString, getMainCurrencyCode } from "@/lib/moneyHelpers";
import PaymentManager from "@/payments";
import config from "@/config";
import { EVENTS, OnboardingStep } from "@/const";
import BaseSpinner from "@/components/BaseSpinner";
import CardCost from "@/components/CardCost";
import ModalView from "@/components/ModalView";

export default defineComponent({
  name: "ModalRefillBalance",
  components: { ModalView, BaseSpinner, CardCost },
  setup() {
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.DEPOSIT]);
    const { creditCardAllowed, hasActivePaymentSystem } = useSystemSettings();
    const { authUserStrict } = useAuthUser();
    const { onboardingData, updateStore: updateOnboarding } = useOnboarding();

    const show = ref(false);
    const loading = ref(false);
    const processingId = ref("");
    const cards = ref<CreditCardAssignment[]>([]);

    const showModal = async () => {
      show.value = true;
      try {
        loading.value = true;
        const requests: Promise<void>[] = [getPaymentPackages()];
        if (creditCardAllowed.value) {
          requests.push(
            providers.CreditCardAssignmentsProvider.findAll({ userId: authUserStrict.value.id }).then(r => {
              cards.value = r;
            })
          );
        }
        await Promise.all(requests);
      } finally {
        loading.value = false;
      }
    };

    const hideModal = () => (show.value = false);

    if (config.featureBalanceRefill) {
      emitter.on<any>(EVENTS.BALANCE_REFILL_SHOW, showModal);
      emitter.on<any>(EVENTS.BALANCE_REFILL_HIDE, hideModal);

      onBeforeUnmount(() => {
        emitter.off<any>(EVENTS.BALANCE_REFILL_SHOW, showModal);
        emitter.off<any>(EVENTS.BALANCE_REFILL_HIDE, hideModal);
      });
    }

    const getCurrencyCost = (paymentPackage: PaymentPackage) => {
      if (paymentPackage.costs.length === 1 && paymentPackage.costs[0].cost === paymentPackage.amount) return [];
      return paymentPackage.costs.map(cost => {
        return formatMoneyString(cost.cost, { currencyCode: cost.currency });
      });
    };

    const packageClick = async (paymentPackage: PaymentPackage) => {
      if (!hasActivePaymentSystem.value) throw new Error("Payment system not found");
      if (processingId.value) return;
      try {
        processingId.value = paymentPackage.id;
        const cost = getCostByCurrency(paymentPackage, getMainCurrencyCode())?.cost;
        const price = (cost && formatMoneyString(cost)) || undefined;
        const response = await PaymentManager.purchase({ pricePackageId: paymentPackage.id, price });
        response?.transaction && emitter.emit(EVENTS.BALANCE_REFILL_PURCHASED, response.transaction);

        if (response.status && !response.subjectPurchased) {
          emitter.emit(EVENTS.NOTIFICATION_SHOW, {
            text: $t("Thank you for your purchase"),
            position: "top",
          });
          if (!onboardingData.value.TOP_UP_BALANCE) {
            updateOnboarding({ [OnboardingStep.TOP_UP_BALANCE]: true });
          }
        }
        yMetrics.paymentPurchase();
        show.value = false;
      } finally {
        processingId.value = "";
      }
    };

    const closeModal = () => {
      if (!processingId.value) show.value = false;
    };

    const pushToCard = (e: Event, navigate: Function) => {
      if (!processingId.value) {
        show.value = false;
        navigate(e);
      } else {
        e.preventDefault();
      }
    };

    return {
      show,
      getCost,
      paymentPackages,
      getCurrencyCost,
      packageClick,
      cards,
      loading,
      processingId,
      closeModal,
      pushToCard,
    };
  },
});
